/**
 * Issue #542 — registering a surface from the client, at last.
 *
 * #165 shipped `POST /grounds/:groundId/facilities` API-only (PR #414
 * touched zero frontend files), so every rating axis #540 built hangs off
 * rows that no client path could create. This module is the RULES of the
 * add gesture — the component (`FacilityAddForm.jsx`) is only the surface —
 * for `lib/conditionUpdate.js`'s reason verbatim: this repo mounts no
 * components in unit tests, so a rule written inside JSX is a rule nothing
 * can assert.
 *
 * --- THE BODY MIRRORS `facilityCreateSchema`, FIELD FOR FIELD ---
 *
 * name (1..120, trimmed), kind (`field`/`net`, #544 — asked and never
 * defaulted, `place_type`'s #136 discipline) and surface (the closed
 * FACILITY_SURFACES set) are required. `is_indoor` and `has_lighting` are REQUIRED BOOLEANS with no
 * default, and the form honours the API's argument for that (#165: "a
 * defaulted lighting flag would fill the directory with venues that merely
 * never answered") by starting both UNANSWERED — a draft with either still
 * `null` is incomplete, not `false`. `condition` is optional and OMITTED
 * when unset: on create, omitted means unrated, which is a state the reader
 * can see (#164). The pin is a PAIR or nothing (`ground_facilities_pin_check`
 * — `pinIsWhole` server-side); half a pin keeps the draft incomplete rather
 * than being sent for the server to refuse.
 *
 * --- WHO MAY ADD: the ground correction gate, mirrored, not widened ---
 *
 * The POST sits behind `checkGroundCorrectionGate` — the same gate as the
 * PATCH the condition update mirrors — so `canAddFacility` is
 * `canOfferGroundEdit` by delegation, exactly as `canUpdateCondition` is.
 * Creator-while-private, or admin (`isVenueAuthority`, #532/Q3): no new
 * authority, no new trust decision, and NEVER a control that 403s. Like
 * every client mirror of this gate it is the SUBSET the client can evaluate
 * honestly; the server also refuses when other players rely on the ground,
 * and that refusal comes back as prose written to be shown.
 */

import { GROUND_PIN_FROM_MAP, canOfferGroundEdit } from "./groundEdit";
import { FACILITY_CONDITIONS, FACILITY_KINDS, FACILITY_SURFACES } from "./facilities";

/**
 * Issue #542's copy, verbatim: the empty state stops being silence. Said
 * only when the directory itself vouches for the zero (see
 * `facilityEmptyState`) — never about a read that failed.
 */
export const FACILITY_EMPTY_COPY = "No fields or nets recorded here yet";

/** Who may register a surface: whoever may correct the venue, and nobody
 * else. Delegation IS the decision (`canUpdateCondition`'s reasoning) — the
 * POST and the PATCH share `checkGroundCorrectionGate`, so their client
 * mirrors must be the same predicate or they drift. */
export function canAddFacility(ground, user) {
  return canOfferGroundEdit(ground, user);
}

/** Where the create goes. A facility is a CHILD of its ground — the API has
 * no other address for one, on purpose. `null` when the ground is not
 * addressable, so a caller cannot POST to `/grounds/undefined/...`. */
export function facilityAddPath(groundId) {
  return groundId ? `/grounds/${groundId}/facilities` : null;
}

/**
 * The blank draft. `is_indoor` / `has_lighting` start as `null` —
 * UNANSWERED, which is not `false` (see the header). `condition` starts
 * unrated, which IS a complete answer. Coordinates are text because they
 * come from inputs; `""` is absent, never zero.
 */
export function emptyFacilityDraft() {
  return {
    name: "",
    kind: null,
    surface: null,
    condition: null,
    is_indoor: null,
    has_lighting: null,
    latitude: "",
    longitude: "",
  };
}

/**
 * `Number("")` is 0 and `Number("  ")` is 0, which is a real coordinate in
 * the Gulf of Guinea (`lib/groundEdit.js`'s `coordinate`, restated here
 * because it is module-private there). A cleared field is "absent".
 */
function coordinate(raw) {
  const text = String(raw ?? "").trim();
  if (text === "") return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

function pinState(draft) {
  const latBlank = String(draft?.latitude ?? "").trim() === "";
  const lngBlank = String(draft?.longitude ?? "").trim() === "";
  if (latBlank && lngBlank) return { ok: true, pin: null };
  const lat = coordinate(draft?.latitude);
  const lng = coordinate(draft?.longitude);
  // Half a pin, an unparseable number, or out of range: incomplete, and the
  // ranges are the schema's (±90 / ±180) so the refusal happens before the
  // round trip rather than as its 400.
  if (lat === null || lng === null) return { ok: false, pin: null };
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return { ok: false, pin: null };
  return { ok: true, pin: { latitude: lat, longitude: lng } };
}

/**
 * The POST body, or `null` while the draft cannot make one. Field for field
 * `facilityCreateSchema` (`api/src/routes/groundFacilities.ts`):
 * `condition` is sent only when a rating was chosen — omitted means
 * unrated — and the pin travels only whole.
 */
export function facilityCreateBody(draft) {
  const name = String(draft?.name ?? "").trim();
  if (!name || name.length > 120) return null;
  // #544, `place_type`'s #136 discipline: asked, never defaulted — the
  // draft starts `null` and `null` is not in the closed set, so Save stays
  // disabled until "Field or net?" is answered.
  if (!FACILITY_KINDS.includes(draft?.kind)) return null;
  if (!FACILITY_SURFACES.includes(draft?.surface)) return null;
  if (draft?.is_indoor !== true && draft?.is_indoor !== false) return null;
  if (draft?.has_lighting !== true && draft?.has_lighting !== false) return null;
  if (draft?.condition != null && !FACILITY_CONDITIONS.includes(draft.condition)) return null;
  const { ok, pin } = pinState(draft);
  if (!ok) return null;
  return {
    name,
    kind: draft.kind,
    surface: draft.surface,
    is_indoor: draft.is_indoor,
    has_lighting: draft.has_lighting,
    ...(draft.condition != null ? { condition: draft.condition } : {}),
    ...(pin ? pin : {}),
  };
}

/** Is the Save button live? The one question, asked of the one builder, so
 * the two can never disagree about what "complete" means. */
export function facilityDraftComplete(draft) {
  return facilityCreateBody(draft) !== null;
}

/**
 * One list, one rule for folding a server row back in — the `onFacilityRated`
 * update generalised so a CREATED row appears exactly like a re-rated one
 * (#542 point 3: the #211 rating block and the condition control key off
 * facility rows, so appending the row IS the refresh — no refetch, no page
 * reload; the response in hand already contains the fact).
 *
 * Always the SERVER'S row, never a locally-guessed one — `condition_rated_at`
 * and `created_at` only the server can stamp (#164's rule, unchanged).
 */
export function upsertFacilityRow(list, row) {
  const rows = list || [];
  if (!row || !row.facility_id) return rows;
  return rows.some((x) => x.facility_id === row.facility_id)
    ? rows.map((x) => (x.facility_id === row.facility_id ? { ...x, ...row } : x))
    : [...rows, row];
}

/**
 * Issue #547 — the pin the add-a-ground draft already holds, restated as the
 * surface draft's pin, for the "add a field or net to this ground instead"
 * route out of the duplicate prompt.
 *
 * The person about to register a second ground at one club has usually
 * already SAID where the surface is — they tapped the map (`presetCoords`)
 * or typed coordinates into the manual form — and the whole point of the
 * third option is that their gesture was right and only its target was
 * wrong. Losing the tap on the way to the right form would be the paper
 * cut; this carries it.
 *
 * `via` is `map` so `FacilityAddForm`'s read-back effect writes it into the
 * coordinate fields, exactly as a tap on the detail map would (#548). The
 * `fromDevice` flag travels WITH the pin when the draft's pin was a device
 * position (#134's near-me "add this place") — carrying it as a DRAFT sends
 * nothing anywhere, and saving it is the same second consent as the form's
 * own "Use my location", which stamps the identical flag. `null` when the
 * draft holds no whole, in-range pin: half a pin is none (`pinState`'s
 * rule), and a surface pin must never be invented.
 */
export function carriedSurfacePin(presetCoords, manual) {
  if (presetCoords && Number.isFinite(presetCoords.lat) && Number.isFinite(presetCoords.lng)) {
    return {
      lat: presetCoords.lat,
      lng: presetCoords.lng,
      via: GROUND_PIN_FROM_MAP,
      ...(presetCoords.fromDevice ? { fromDevice: true } : {}),
    };
  }
  const { ok, pin } = pinState(manual || {});
  if (!ok || !pin) return null;
  return { lat: pin.latitude, lng: pin.longitude, via: GROUND_PIN_FROM_MAP };
}

/**
 * Issue #547 — what the person routed to an existing ground is told when the
 * gate does NOT let them add the surface themselves. The route must exist
 * for everyone (the mistake it catches is everyone's), but the add form is
 * `canAddFacility`-gated like every facility write, so the ineligible
 * arrival gets the honest sentence instead of a control that 403s (#271).
 */
export const SURFACE_INSTEAD_NOTE =
  "This ground is already in the directory — its record keeper or an admin can add its fields and nets. Once you've played here, you can rate what's listed.";

/**
 * What an EMPTY facility list may honestly say — called only when the loaded
 * list has zero rows. Three deliberate states (#542 point 2, on #271's
 * discipline):
 *
 *   "invite"  — this person may add, so the silence becomes the affordance.
 *   "none"    — genuinely none recorded; say so ("No fields or nets
 *               recorded here yet") instead of rendering nothing a player
 *               could mistake for the feature not existing.
 *   "unknown" — no claim can be made, render NOTHING. Two ways here: the
 *               directory's own derived `facility_count` (#418, on every
 *               ground `GET /grounds` returns) says rows exist that this
 *               page's facilities read did not deliver — the read failed or
 *               has not answered, and "no fields here" would be #271's
 *               exact lie; or the ground carries no `facility_count` at all,
 *               which is a played-only synthetic row (`GroundsMap.jsx`'s
 *               `enriched`) whose facilities the API deliberately never
 *               sends — absence of information, not a claim of none.
 */
export function facilityEmptyState(ground, canAdd) {
  const recorded = ground?.facility_count;
  if (recorded == null || recorded > 0) return "unknown";
  return canAdd ? "invite" : "none";
}
